"use client";

import { cn } from "utils";

import { AppImage } from "@/src/components/NextImage";
import { ShellLink } from "@/src/components";
import { ThemeSymbols } from "./ThemeSymbols";
import { ThemeWithMarketData } from "./type";

export interface ThemeCardV2Props {
  href?: string;
  itemData: ThemeWithMarketData;
  className?: string;
  showSymbols?: boolean;
  descClassName?: string;
}

/**
 * ThemeCardV2 组件
 * 参考 Figma 节点 67452:63792
 * 2.0 版本的卡片设计，包含可展开/收起的描述文字
 */
export const ThemeCardV2 = (props: ThemeCardV2Props) => {
  const {
    href,
    itemData,
    className,
    showSymbols = false,
    descClassName = "",
  } = props;
  const { slug, theme, desc, image, symbols, performance } = itemData;

  // 处理图片 URL
  const imageSrc = image || "/assets/images/image_place_holder.png";

  // 格式化百分比 - 参照 Theme24HPerformanceUI 的处理方式
  const formatPercentage = (
    percentage?: number | string | null,
  ): { formatted: string; isPositive: boolean; isValid: boolean } => {
    // 处理无数据情况
    if (typeof percentage === "undefined" || percentage === null) {
      return { formatted: "--", isPositive: false, isValid: false };
    }

    const priceChange = Number(percentage);
    const isZero = Math.abs(priceChange) < 0.01;

    // 处理零值情况
    if (isZero) {
      return { formatted: "0%", isPositive: true, isValid: true };
    }

    // 格式化百分比值 - 参照 Theme24HPerformanceUI 的逻辑
    const priceChangePercentage = priceChange;
    const formattedValue = `${((priceChangePercentage / 100) * 100).toFixed(2)}%`;

    // 根据正负值判断
    const isPositive = priceChange >= 0;

    return {
      formatted: `${isPositive ? "+" : ""}${formattedValue}`,
      isPositive,
      isValid: true,
    };
  };

  const percentage = performance?.percentage;
  const {
    formatted: formattedPercentage,
    isPositive,
    isValid,
  } = formatPercentage(percentage);

  return (
    <ShellLink
      aria-label="Theme Card V2"
      data-openreplay-label={`Theme Card V2 Link | ${href || "#"}`}
      id="lnk-theme-card-v2"
      href={href || "#"}
      className={cn(
        "group rounded-3 bg-background_solid_neutral_primary overflow-hidden transition-transform block relative h-[206px]",
        className,
      )}
    >
      {/* 背景图片 */}
      <div className="absolute z-0 w-full h-20">
        <AppImage
          src={imageSrc}
          alt={slug || ""}
          width={640}
          height={360}
          className="w-full h-full object-cover"
        />
      </div>
      {/* 内容区域 - 显示在遮罩层之上，靠下对齐 */}
      <div className="absolute bottom-0 left-0 right-0 z-[2] flex flex-col gap-3 p-3">
        {/* 标题和性能指标行 */}
        <div className="flex flex-col">
          {/* 标题 */}
          <h3
            aria-label="Theme Card V2 h3"
            data-openreplay-label="Theme Card V2 h3 Semantic"
            id="sem-theme-card-v2-h3"
            className="title_l text-content_primary line-clamp-2 mb-0.5"
          >
            {theme || ""}
          </h3>

          {/* 百分比和 symbols 行 */}
          <div className="flex items-center justify-between w-full">
            {/* 百分比 */}
            {isValid && (
              <span
                className={cn("body_bold_s", {
                  "text-content_accent1_primary": isPositive,
                  "text-content_accent2_primary": !isPositive,
                })}
              >
                {formattedPercentage}
              </span>
            )}

            {/* Symbols */}
            {showSymbols && symbols && symbols.length > 0 && (
              <div className="flex-shrink-0">
                <ThemeSymbols symbols={symbols} className="mt-0" size={15} />
              </div>
            )}
          </div>
        </div>

        {/* 描述文字区域 */}
        {desc && (
          <p
            className={cn(
              "body_s text-content_primary line-clamp-2 flex-wrap",
              descClassName,
            )}
          >
            {desc}
          </p>
        )}
      </div>
    </ShellLink>
  );
};
